import "./SudokuSolver.css";
import { useRef, useEffect, useState } from "react";
import { SudokuCell } from "./SudokuCell";
import { Application } from "../../System/Application/Application";

const LABELS = {
    startAutoSolve: { En: "Start Auto Solve", Fr: "Résolution auto" },
    stopAutoSolve: { En: "Stop Auto Solve", Fr: "Arrêter" },
    clearGrid: { En: "Clear Grid", Fr: "Effacer" },
    enableAutoSelect: { En: "Enable Auto Select", Fr: "Auto-sélection" },
    disableAutoSelect: { En: "Disable Auto Select", Fr: "Désactiver" },
    backToLastState: { En: "Back to last state", Fr: "Annuler" },
};

export function SudokuSolver(props) {
    const lang = props.language ?? "En";

    const [autoSolve, setAutoSolve] = useState(false);
    const [autoSelect, setAutoSelect] = useState(false);
    const [selectedDigit, setSelectedDigit] = useState(null);

    const [digitsGrid, setDigitsGrid] = useState(() =>
        Array.from({ length: 9 }, () => Array(9).fill(null))
    );
    const [lastDigitsGrid, setLastDigitsGrid] = useState(
        Array.from({ length: 9 }, () => Array(9).fill(null))
    );
    const [possibleDigitsGrid, setPossibleDigitsGrid] = useState(
        Array.from({ length: 9 }, () => Array(9).fill([]))
    );

    const gridRef = useRef(null);
    const gridAreaRef = useRef(null);
    const [gridSize, setGridSize] = useState(0);

    useEffect(() => {
        const observer = new ResizeObserver(([entry]) => {
            const { width, height } = entry.contentRect;
            setGridSize(Math.min(width, height));
        });
        if (gridAreaRef.current) observer.observe(gridAreaRef.current);
        return () => observer.disconnect();
    }, []);

    const onClear = (x, y) => {
        setDigitsGrid((prevGrid) => {
            const newGrid = prevGrid.map((row) => [...row]);
            newGrid[y][x] = null;
            return newGrid;
        });
        setSelectedDigit(null);
    };

    const onSelect = (digit) => {
        setSelectedDigit((prev) => (prev === digit ? null : digit));
    };

    const onDigitChange = (x, y, digit, userRequest = false) => {
        setDigitsGrid((prevGrid) => {
            if (userRequest) setLastDigitsGrid(prevGrid);
            const newGrid = prevGrid.map((row) => [...row]);
            newGrid[y][x] = digit;
            return newGrid;
        });
        if (autoSelect && userRequest) setSelectedDigit(digit);
    };

    const getPossibleDigitsOnRow = (row) =>
        [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((d) => !digitsGrid[row].includes(d));

    const getPossibleDigitsOnCol = (col) =>
        [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((d) => !digitsGrid.some((row) => row[col] === d));

    const getPossibleDigitsOnBox = (row, col) => {
        const boxRowStart = Math.floor(row / 3) * 3;
        const boxColStart = Math.floor(col / 3) * 3;
        const boxDigits = new Set();
        for (let r = boxRowStart; r < boxRowStart + 3; r++)
            for (let c = boxColStart; c < boxColStart + 3; c++)
                if (digitsGrid[r][c] !== null) boxDigits.add(digitsGrid[r][c]);
        return [1, 2, 3, 4, 5, 6, 7, 8, 9].filter((d) => !boxDigits.has(d));
    };

    const getPossibleDigits = (row, col) => {
        const row_ = getPossibleDigitsOnRow(row);
        const col_ = getPossibleDigitsOnCol(col);
        const box_ = getPossibleDigitsOnBox(row, col);
        return row_.filter((d) => col_.includes(d)).filter((d) => box_.includes(d));
    };

    const updatePossibleGrid = (possGrid) => {
        for (let row = 0; row < 3; row++) {
            for (let col = 0; col < 3; col++) {
                const count = Array(9).fill(0);
                const last = Array(9).fill(null);
                for (let subX = 0; subX < 3; subX++) {
                    for (let subY = 0; subY < 3; subY++) {
                        const x = col * 3 + subX;
                        const y = row * 3 + subY;
                        if (digitsGrid[y][x] == null) {
                            for (const d of possGrid[y][x]) {
                                count[d - 1]++;
                                last[d - 1] = { X: x, Y: y };
                            }
                        }
                    }
                }
                for (let d = 0; d < 9; d++) {
                    if (count[d] === 1) onDigitChange(last[d].X, last[d].Y, d + 1);
                }
            }
        }
    };

    useEffect(() => {
        const prepGrid = Array.from({ length: 9 }, (_, row) =>
            Array.from({ length: 9 }, (_, col) => getPossibleDigits(row, col))
        );
        if (autoSolve) updatePossibleGrid(prepGrid);
        setPossibleDigitsGrid(prepGrid);
    }, [gridRef, digitsGrid, selectedDigit, autoSolve]);

    return (
        <Application {...props}>
            <div className="sudoku-app">
                <div className="sudoku-grid" ref={gridAreaRef}>
                    <div
                        className="sudoku-grid-container"
                        ref={gridRef}
                        style={{ width: gridSize, height: gridSize }}
                    >
                        {digitsGrid.map((row, rowIndex) => (
                            <div className="sudoku-row" key={rowIndex}>
                                {row.map((cellValue, colIndex) => (
                                    <div
                                        className="sudoku-cell-wrapper"
                                        key={`${rowIndex}-${colIndex}`}
                                    >
                                        <SudokuCell
                                            posX={colIndex}
                                            posY={rowIndex}
                                            value={cellValue}
                                            possibleDigits={possibleDigitsGrid[rowIndex][colIndex]}
                                            selectedDigit={selectedDigit}
                                            autoSolve={autoSolve}
                                            onDigitChange={onDigitChange}
                                            onClear={onClear}
                                            onSelect={onSelect}
                                        />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="sudoku-controls">
                    <button className="sudoku-button" onClick={() => setAutoSolve(!autoSolve)}>
                        {autoSolve ? LABELS.stopAutoSolve[lang] : LABELS.startAutoSolve[lang]}
                    </button>
                    <button
                        className="sudoku-button"
                        onClick={() =>
                            setDigitsGrid(Array.from({ length: 9 }, () => Array(9).fill(null)))
                        }
                    >
                        {LABELS.clearGrid[lang]}
                    </button>
                    <button className="sudoku-button" onClick={() => setAutoSelect(!autoSelect)}>
                        {autoSelect ? LABELS.disableAutoSelect[lang] : LABELS.enableAutoSelect[lang]}
                    </button>
                    <button
                        className="sudoku-button"
                        onClick={() => {
                            setAutoSolve(false);
                            setDigitsGrid(lastDigitsGrid);
                        }}
                    >
                        {LABELS.backToLastState[lang]}
                    </button>
                </div>
            </div>
        </Application>
    );
}
